import { useState, useEffect, useMemo, useCallback } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from "material-react-table";
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SearchIcon from '@mui/icons-material/Search';
import { IListaCbo } from "../../../../../../../application/models/geo-veles/IListaCbo";
import { useListaCboService } from "../../../../../../../use-cases/geo-veles/lista-cbo";
import { useAppSelector } from "../../../../../../hooks/store-hooks";
import { useRecortesExtracaoService } from "../../../../../../../use-cases/geo-veles";

// Interface para os dados de CBO
interface CboData {
  id: number;
  codigo: string;
  descricao: string;
  display: string;
}

// Interface para listas filtradas
interface ListaFiltrada {
  lista: IListaCbo;
  cbosFiltrados: CboData[];
  nomeCorresponde: boolean;
}

// Componente individual para cada lista
const ListaCboVirtualTable = ({
  lista,
  cbosFiltrados,
  mostraTodosCbos
}: {
  lista: IListaCbo;
  cbosFiltrados: CboData[];
  mostraTodosCbos: boolean;
}) => {
  const columns = useMemo<MRT_ColumnDef<CboData>[]>(
    () => [
      {
        accessorKey: 'display',
        header: 'CBO',
        size: 300,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem'
            }}
          >
            {cell.getValue<string>()}
          </Typography>
        ),
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: cbosFiltrados,
    enablePagination: false,
    enableRowNumbers: false,
    enableToolbarInternalActions: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableSorting: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    // Configurações de virtualização para performance
    enableRowVirtualization: true,
    enableStickyHeader: true,
    // Ajustes de layout responsivo
    layoutMode: 'grid',
    // Configurações de desempenho
    defaultDisplayColumn: {
      enableResizing: false,
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: '400px',
        minHeight: '200px',
      },
    },
    muiTableBodyRowProps: {
      sx: {
        height: '40px',
      },
    },
    muiTableBodyCellProps: {
      sx: {
        padding: '8px 16px',
        borderBottom: '1px solid #e0e0e0',
      },
    },
  });

  const totalCbos = useMemo(() => {
    return cbosFiltrados.length;
  }, [cbosFiltrados]);

  return (
    <Paper elevation={1} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1, flexShrink: 0 }}>
        <FormatListBulletedIcon color="primary" />
        <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
          {lista.Nome || `Lista ${lista.IdVelesGeoListaCBO}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {mostraTodosCbos
            ? `${totalCbos} CBO(s)`
            : `${cbosFiltrados.length} de ${totalCbos} CBO(s)`
          }
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <MaterialReactTable table={table} />
      </Box>
    </Paper>
  );
};

interface RecorteListasCboFilterEditPreviewProps {
  listasCboFilter?: number[];
}

export const RecorteListasCboFilterEditPreview = ({
  listasCboFilter: listasCboFilterProp,
}: RecorteListasCboFilterEditPreviewProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [listasCarregadas, setListasCarregadas] = useState<IListaCbo[]>([]);
  const [loading, setLoading] = useState(true);

  const editOpenForm = useAppSelector((state) => state.recorteExtracao.editOpenForm);
  const listasCboDoFormulario = useAppSelector(
    (state) => state.recorteExtracao.editRecorteExtracaoForm.fields.Filtros?.listasCbo
  );

  const { removeEditRecorteFormFiltros } = useRecortesExtracaoService();
  const { getListaCboById } = useListaCboService();

  const listasCboFilter = editOpenForm ? listasCboDoFormulario : listasCboFilterProp;

  const hasListasCbo = useMemo(() => {
    return listasCboFilter && listasCboFilter.length > 0;
  }, [listasCboFilter]);

  const handleRemoveFilter = () => {
    removeEditRecorteFormFiltros({ filtro: 'listasCbo' });
  };

  const carregarListas = useCallback(async () => {
    if (!hasListasCbo) {
      setListasCarregadas([]);
      setLoading(false);
      return;
    }

    const idsUnicos = Array.from(new Set(listasCboFilter));
    setLoading(true);
    const listasDetalhadas: IListaCbo[] = [];

    for (const id of idsUnicos) {
      try {
        const lista = await getListaCboById(id);
        listasDetalhadas.push(lista);
      } catch (error) {
        console.error(`❌ Erro ao carregar lista de CBO com ID ${id}:`, error);
      }
    }

    setListasCarregadas(listasDetalhadas);
    setLoading(false);
  }, [hasListasCbo, listasCboFilter, getListaCboById]);

  useEffect(() => {
    carregarListas();
  }, [carregarListas]);

  const parseCbosFromStringArray = useCallback(
    (cbosArray: string[] | null | undefined): CboData[] => {
      if (!cbosArray || !Array.isArray(cbosArray)) return [];

      return cbosArray
        .map((item, index) => {
          if (typeof item !== 'string') return null;
          const trimmed = item.trim();
          if (trimmed === '') return null;
          const dashIndex = trimmed.indexOf(' - ');

          if (dashIndex === -1) {
            return {
              id: index,
              codigo: trimmed,
              descricao: '',
              display: trimmed
            };
          }

          const codigo = trimmed.substring(0, dashIndex).trim();
          const descricao = trimmed.substring(dashIndex + 3).trim();
          return {
            id: index,
            codigo,
            descricao,
            display: `${codigo} - ${descricao}`
          };
        })
        .filter((item): item is CboData => item !== null);
    },
    []
  );

  // Filtra todas as listas baseado no termo de busca geral
  const listasFiltradas = useMemo((): ListaFiltrada[] => {
    if (listasCarregadas.length === 0) return [];

    if (!searchTerm.trim()) {
      // Se não há busca, mostra todas as listas com todos os CBOs
      return listasCarregadas.map((lista) => {
        const cbos = parseCbosFromStringArray(lista.CBOs);
        return {
          lista,
          cbosFiltrados: cbos,
          nomeCorresponde: true,
        };
      });
    }

    const term = searchTerm.toLowerCase();

    return listasCarregadas
      .map((lista) => {
        const nomeCorresponde = (lista.Nome?.toLowerCase() || '').includes(term);
        const todosCbos = parseCbosFromStringArray(lista.CBOs);

        const cbosFiltrados = todosCbos.filter((cbo) =>
          cbo.codigo.toLowerCase().includes(term) ||
          cbo.descricao.toLowerCase().includes(term) ||
          cbo.display.toLowerCase().includes(term)
        );

        return { lista, cbosFiltrados, nomeCorresponde };
      })
      .filter((listaFiltrada) =>
        listaFiltrada.nomeCorresponde || listaFiltrada.cbosFiltrados.length > 0
      );
  }, [listasCarregadas, searchTerm, parseCbosFromStringArray]);

  // Calcula o layout responsivo baseado na quantidade de listas
  const getGridLayout = () => {
    const count = listasFiltradas.length;
    if (count === 0) return { xs: 12 };
    if (count === 1) return { xs: 12 };
    return { xs: 12, md: 6 }; // Máximo 2 por linha
  };

  if (editOpenForm && !hasListasCbo) {
    return null;
  }

  if (!hasListasCbo) {
    return null;
  }

  return (
    <Grid item container xs={12} spacing={2} mb={2} p={2}>
      <Paper elevation={3} sx={{ padding: 2, width: "100%" }}>
        <Grid container justifyContent="space-between" alignItems="center" mb={2}>
          <Typography
            variant="body1"
            color="primary"
            fontWeight="bold"
            textTransform="uppercase"
          >
            Listas de CBO ({listasCarregadas.length})
          </Typography>

          {/* 🔸 Só mostra o botão se estiver em modo de edição */}
          {editOpenForm && (
            <Button
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={handleRemoveFilter}
              size="small"
            >
              Remover filtro
            </Button>
          )}
        </Grid>

        {loading ? (
          <Grid container justifyContent="center" my={2}>
            <CircularProgress size={24} />
          </Grid>
        ) : (
          <>
            {/* Barra de busca GERAL única */}
            <TextField
              size="small"
              placeholder="Pesquisar por nome da lista, código ou descrição do CBO..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                ),
              }}
            />

            {/* Grid responsivo com listas lado a lado (máximo 2 por linha) */}
            <Grid container spacing={2}>
              {listasFiltradas.length > 0 ? (
                listasFiltradas.map((listaFiltrada) => (
                  <Grid item {...getGridLayout()} key={listaFiltrada.lista.IdVelesGeoListaCBO.toString()}>
                    <ListaCboVirtualTable
                      lista={listaFiltrada.lista}
                      cbosFiltrados={listaFiltrada.cbosFiltrados}
                      mostraTodosCbos={!searchTerm.trim()}
                    />
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                      Nenhuma lista ou CBO encontrado para "{searchTerm}"
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </Paper>
    </Grid>
  );
};